import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { Reservation, ReservationList, SlotCapacityCheck } from '@restaurant/api-client';
import { useQuery } from '@tanstack/react-query';

// Polling backstop for the live SSE stream (useReservationsRealtime). SSE
// delivers updates instantly; this guarantees a booking is never missed for
// more than this interval even if the stream drops. Refetch-on-focus catches
// the case where the device was asleep/backgrounded.
const RESERVATION_POLL_MS = 1000 * 30;

export function useReservationList(date?: string) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);

  return useQuery({
    queryKey: ['reservations', 'list', restaurantId, date],
    queryFn: async () => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<ReservationList>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/reservations', {
          params: { path: { restaurantId }, query: { date, page: 1, limit: 1000 } },
        }),
      );
    },
    enabled: !!restaurantId,
    refetchInterval: RESERVATION_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/**
 * Slot capacity probe for the create form. Asks the backend whether a specific
 * date+time can still seat `partySize` under the shift's configured caps, so the
 * form can warn before staff overbook a full slot (staff are still allowed to -
 * the backend records the override - but they get an explicit heads-up first).
 * Disabled until all inputs are present; the result is cheap and short-lived.
 */
export function useSlotCapacity(date: string, time: string, partySize: number) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);

  return useQuery({
    queryKey: ['reservations', 'slot-capacity', restaurantId, date, time, partySize],
    queryFn: async () => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<SlotCapacityCheck>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/reservations/capacity-check', {
          params: { path: { restaurantId }, query: { date, time, partySize } },
        }),
      );
    },
    enabled: !!restaurantId && !!date && !!time && partySize > 0,
    staleTime: 1000 * 15,
  });
}

export function usePendingReservations() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);

  return useQuery({
    queryKey: ['reservations', 'pending', restaurantId],
    queryFn: async () => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<Reservation[]>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/reservations/pending', {
          params: { path: { restaurantId }, query: { page: 1, limit: 1000 } },
        }),
      );
    },
    enabled: !!restaurantId,
    refetchInterval: RESERVATION_POLL_MS,
    refetchOnWindowFocus: true,
  });
}
